import { useTranslation } from 'react-i18next';

import { useBillitAuthorizationUrl, useUnlinkBillit, useUnlinkCashaca, useUpdateInvoiceForwardingSettings, useUser } from '~/api/user';
import billitImage from '~/assets/images/partners/billit.png';
import cashacaImage from '~/assets/images/partners/cashaca.png';
import dexxterImage from '~/assets/images/partners/dexxter.png';
import { useDialog } from '~/hooks/useDialog';
import { toast } from '~/utils/toast';

import { ConfirmExtraIntegrationDialog } from '../Peppol/ConfirmExtraIntegrationDialog';
import { useConfirmExtraIntegration } from '../Peppol/ConfirmExtraIntegrationDialog.hooks';
import { PeppolForwardingWarning } from '../Peppol/PeppolForwardingWarning';
import { CashacaModal } from './CashacaModal';
import { DexxterModal } from './DexxterModal';
import { ExternalSystemCard } from './ExternalSystemCard';
import { systemsStyle, wrapperStyle } from './ExternalSystems.css';

export const ExternalSystems = () => {
  const { data: user } = useUser();

  const { t } = useTranslation(['common', 'settings']);

  const linkBilitMutation = useBillitAuthorizationUrl();
  const unlinkBillitMutation = useUnlinkBillit();
  const unlinkCashacaMutation = useUnlinkCashaca();
  const updateAutoForwardingMutation = useUpdateInvoiceForwardingSettings();

  const dexxterModal = useDialog();
  const cashacaModal = useDialog();
  const confirmExtraIntegration = useConfirmExtraIntegration();

  const pairBillit = () => {
    linkBilitMutation.mutate(undefined, {
      onSuccess: (authorizationUrl) => {
        location.href = authorizationUrl;
      },
    });
  };

  const isDexxterPaired = user.invoiceForwardingSettings.some((setting) => /@(inbound|expense)\.dexxter\.be/.test(setting.receiverEmail));
  const isBillitPaired = user.integrations.billit;
  const isCashacaPaired = user.integrations.cashaca;

  return (
    <div className={wrapperStyle}>
      {(isDexxterPaired || isBillitPaired || isCashacaPaired) && (
        <PeppolForwardingWarning description={t('settings:integrations.externalSystems.peppolWarning')} />
      )}

      <ul className={systemsStyle}>
        <ExternalSystemCard
          imageUrl={dexxterImage}
          isPaired={isDexxterPaired}
          isUnpairing={updateAutoForwardingMutation.isPending}
          onPair={() => confirmExtraIntegration.confirm(dexxterModal.open)}
          onUnpair={() =>
            updateAutoForwardingMutation.mutate(
              {
                entries: user.invoiceForwardingSettings.filter((setting) => !/@(inbound|expense)\.dexxter\.be/.test(setting.receiverEmail)),
              },
              { onError: () => toast.danger({ description: t('common:error') }) },
            )
          }
          system="dexxter"
        />

        <ExternalSystemCard
          disabled={linkBilitMutation.isPending}
          imageUrl={billitImage}
          isPaired={isBillitPaired}
          isUnpairing={unlinkBillitMutation.isPending}
          onPair={() => confirmExtraIntegration.confirm(pairBillit)}
          onUnpair={() => unlinkBillitMutation.mutate()}
          system="billit"
        />

        <ExternalSystemCard
          imageUrl={cashacaImage}
          isPaired={isCashacaPaired}
          isUnpairing={unlinkCashacaMutation.isPending}
          onPair={() => confirmExtraIntegration.confirm(cashacaModal.open)}
          onUnpair={() => unlinkCashacaMutation.mutate()}
          system="cashaca"
        />
      </ul>

      {dexxterModal.isOpen && <DexxterModal onClose={dexxterModal.close} />}
      {cashacaModal.isOpen && <CashacaModal onClose={cashacaModal.close} />}

      <ConfirmExtraIntegrationDialog handle={confirmExtraIntegration.handle} />
    </div>
  );
};
